// Scratch Pad Panel for Organizer - Slide-in from right
import { useState, useMemo } from 'react';
import { X, Plus, Trash2, Edit2, Save, StickyNote } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEntity } from '@/hooks/useEntity';
import { scratchPadEntityConfig } from '@/entities/ScratchPad';

interface OrganizerScratchPadPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialContent?: string;
  initialTitle?: string;
}

type ScratchPadEntry = {
  id: number;
  documentId: number;
  userId: number;
  title: string;
  content: string;
  tags: string;
  position?: number;
  created_at: string;
  updated_at: string;
};

export default function OrganizerScratchPadPanel({ 
  isOpen, 
  onClose,
  initialContent,
  initialTitle 
}: OrganizerScratchPadPanelProps) {
  const { user } = useAuth();
  const { items, create, remove, update, reload } = useEntity<ScratchPadEntry>(scratchPadEntityConfig);
  
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ 
    title: initialTitle || '', 
    content: initialContent || '' 
  });

  // Filter to user's general notes (documentId = 0 for organizer notes)
  const userNotes = useMemo(() => {
    return items
      .filter(item => item.userId === user?.id && item.documentId === 0)
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [items, user?.id]);

  const handleSave = async () => {
    if (!formData.title.trim() || !user) return;

    try {
      if (editingId) {
        await update(editingId, {
          title: formData.title.trim(),
          content: formData.content.trim(),
        });
        setEditingId(null);
      } else {
        await create({
          documentId: 0, // 0 = organizer general notes
          userId: user.id,
          title: formData.title.trim(),
          content: formData.content.trim(),
          tags: '[]',
        });
      }
      setFormData({ title: '', content: '' });
      setShowForm(false);
      await reload();
    } catch (error) {
      console.error('Failed to save note:', error);
      alert('Failed to save note');
    }
  };

  const handleEdit = (note: ScratchPadEntry) => {
    setFormData({ title: note.title, content: note.content });
    setEditingId(note.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this note?')) return;
    try {
      await remove(id);
      await reload();
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleNewNote = () => {
    setFormData({ title: initialTitle || '', content: initialContent || '' });
    setEditingId(null);
    setShowForm(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-[500px] bg-gradient-to-b from-[#0a0513] via-[#0f051a] to-[#1a0a2e] border-l-2 border-amber-400/30 shadow-2xl z-[9999] transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-amber-400/20 bg-gradient-to-r from-amber-600/20 to-orange-600/20">
          <div>
            <h2 className="text-xl font-bold text-white">Scratch Pad</h2>
            <p className="text-xs text-amber-300">Quick notes and ideas</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleNewNote}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              title="New Note"
            >
              <Plus className="w-5 h-5 text-amber-300" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-amber-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-80px)] p-6">
          {showForm ? (
            /* Form */
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-amber-200 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-amber-400/30 text-white placeholder-white/40 focus:outline-none focus:border-amber-400"
                  placeholder="Note title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-200 mb-2">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  rows={10}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-amber-400/30 text-white placeholder-white/40 focus:outline-none focus:border-amber-400"
                  placeholder="Write your note here..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!formData.title.trim()}
                  className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold hover:from-amber-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setFormData({ title: '', content: '' });
                  }}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          {/* Notes List */}
          <div className="space-y-3">
            {userNotes.length === 0 ? (
              <div className="text-center py-12">
                <StickyNote className="w-12 h-12 text-amber-400/30 mx-auto mb-4" />
                <p className="text-white/60">No notes yet</p>
                <p className="text-sm text-white/40 mt-2">Click + to create your first note</p>
              </div>
            ) : (
              userNotes.map(note => (
                <div
                  key={note.id}
                  className="p-4 rounded-lg bg-white/5 border border-amber-400/20 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-semibold">{note.title}</h3>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(note)}
                        className="p-1 rounded hover:bg-white/10"
                      >
                        <Edit2 className="w-4 h-4 text-amber-300" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="p-1 rounded hover:bg-white/10"
                      >
                        <Trash2 className="w-4 h-4 text-red-300" />
                      </button>
                    </div>
                  </div>
                  <p className="text-white/70 text-sm whitespace-pre-wrap line-clamp-3">
                    {note.content}
                  </p>
                  <p className="text-xs text-white/40 mt-2">
                    {new Date(note.updated_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
